import { createClient } from "@/lib/supabase/client"

export interface Conversation {
  id: string
  tenant_id: string // Added tenant_id per schema
  user_id: string
  mode: string
  title: string | null
  created_at: string
  updated_at: string
}

export interface Message {
  id: string
  conversation_id: string
  role: string
  content: string
  metadata?: Record<string, any>
  created_at: string
}

/**
 * Create a new conversation (client-side)
 * Returns conversation ID for routing
 */
export async function createConversation(userId: string, tenantId: string, mode = "advisory"): Promise<string | null> {
  try {
    const supabase = createClient()

    const timestamp = new Date().toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    })
    const initialTitle = `Chat • ${timestamp}`

    const { data, error } = await supabase
      .from("conversations")
      .insert({
        tenant_id: tenantId,
        user_id: userId,
        mode,
        title: initialTitle,
      })
      .select("id")
      .single()

    if (error) {
      console.error("[v0] Failed to create conversation:", error)
      return null
    }

    return data.id
  } catch (error) {
    console.error("[v0] Exception creating conversation:", error)
    return null
  }
}

/**
 * Save a message to a conversation (client-side)
 */
export async function saveMessage(
  conversationId: string,
  role: "user" | "assistant",
  content: string,
  metadata?: Record<string, any>,
): Promise<boolean> {
  try {
    const supabase = createClient()

    const { error } = await supabase.from("messages").insert({
      conversation_id: conversationId,
      role,
      content,
      metadata: metadata || {},
    })

    if (error) {
      console.error("[v0] Failed to save message:", error)
      return false
    }

    // Update conversation updated_at timestamp
    await supabase.from("conversations").update({ updated_at: new Date().toISOString() }).eq("id", conversationId)

    return true
  } catch (error) {
    console.error("[v0] Exception saving message:", error)
    return false
  }
}

/**
 * Load messages for a conversation (client-side)
 */
export async function loadMessages(conversationId: string): Promise<Message[]> {
  try {
    const supabase = createClient()

    const { data, error } = await supabase
      .from("messages")
      .select("*")
      .eq("conversation_id", conversationId)
      .order("created_at", { ascending: true })

    if (error) {
      console.error("[v0] Failed to load messages:", error)
      return []
    }

    return data || []
  } catch (error) {
    console.error("[v0] Exception loading messages:", error)
    return []
  }
}

/**
 * Get user's conversations (client-side)
 */
export async function getUserConversations(userId: string, limit = 50): Promise<Conversation[]> {
  try {
    const supabase = createClient()

    const { data, error } = await supabase
      .from("conversations")
      .select("*")
      .eq("user_id", userId)
      .order("updated_at", { ascending: false })
      .limit(limit)

    if (error) {
      console.error("[v0] Failed to load conversations:", error)
      return []
    }

    return data || []
  } catch (error) {
    console.error("[v0] Exception loading conversations:", error)
    return []
  }
}

/**
 * Update conversation title (simple heuristic - first 7 words)
 */
export async function updateConversationTitle(conversationId: string, firstUserMessage: string): Promise<void> {
  try {
    const supabase = createClient()

    const sanitized = firstUserMessage.trim().replace(/\s+/g, " ")
    let title = sanitized

    if (title.length > 50) {
      // Truncate at word boundary
      title = title.substring(0, 47)
      const lastSpace = title.lastIndexOf(" ")
      if (lastSpace > 30) {
        title = title.substring(0, lastSpace)
      }
      title += "..."
    }

    await supabase.from("conversations").update({ title }).eq("id", conversationId)
  } catch (error) {
    console.error("[v0] Exception updating conversation title:", error)
  }
}

/**
 * Confirm a draft conversation (make it visible in recents)
 * NOTE: is_draft removed from schema - this is now a no-op but kept for backwards compat
 */
export async function confirmConversation(conversationId: string): Promise<boolean> {
  try {
    const supabase = createClient()

    const { error } = await supabase
      .from("conversations")
      .update({ updated_at: new Date().toISOString() })
      .eq("id", conversationId)

    if (error) {
      console.error("[v0] Failed to update conversation:", error)
      return false
    }

    return true
  } catch (error) {
    console.error("[v0] Exception updating conversation:", error)
    return false
  }
}
